import { EditIcon } from '@/assets/EditIcon';
import { ROUTE_PARTIES_EDIT } from '@/common/constants';
import { MODAL_KEYS } from '@/common/constants/modal.keys';
import { Button } from '@/components/ui/button';
import useMeasureStore from '@/features/loads/components/general/store/useMeasureStore';
import { useModal } from '@/hooks/useModal';
import { ColumnDef } from '@tanstack/react-table';
import { PackageMinusIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IParty } from '../../models';
import { usePartyStore } from '../../store/usePartyStore';

export const createColumns = (): ColumnDef<IParty>[] => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    cropTypes,
    generationTypes,
    cropGenerations,
    storageTypes,
    setPartyDetail,
  } = usePartyStore();
  const { harvestType } = useMeasureStore();
  const { openModal } = useModal(MODAL_KEYS.CLOSE_PARTY);
  const [searchParams, setSearchParams] = useSearchParams();

  return useMemo(
    () => [
      {
        accessorKey: 'place_number',
        header: () => t('place.number'),
      },
      {
        accessorKey: 'party_number',
        header: () => t('party.number'),
      },
      {
        accessorKey: 'cluster_name',
        header: () => t('claster'),
      },
      {
        accessorKey: 'capacity',
        header: () => t('capacity'),
      },
      {
        accessorKey: 'loaded',
        header: () => t('loaded.cotton'),
        cell: ({ row }) => row.original.loaded ?? 0,
      },
      {
        accessorKey: 'free_space',
        header: () => t('free.space'),
        cell: ({ row }) => row.original.capacity - row.original.loaded,
      },
      {
        accessorKey: 'crop_type_id',
        header: () => t('selection.type'),
        cell: ({ row }) =>
          cropTypes?.find((item) => item.value == row.original.crop_type_id)
            ?.label,
      },
      {
        accessorKey: 'generation',
        header: () => t('generation.type'),
        cell: ({ row }) =>
          generationTypes?.find(
            (item) => item.value === row.original.generation,
          )?.label,
      },
      {
        accessorKey: 'crop_generation_id',
        header: () => t('generation'),
        cell: ({ row }) =>
          cropGenerations?.find(
            (item) => item.value === row.original.crop_generation_id,
          )?.label || '---',
      },
      {
        accessorKey: 'storage_type',
        header: 'Saqlash turi',
        cell: ({ row }) =>
          storageTypes?.find(
            (item) => item.value === row.original?.storage_type,
          )?.label || '---',
      },
      {
        accessorKey: 'industrial_grade',
        header: () => t('industry.type'),
      },
      {
        accessorKey: 'class',
        header: () => t('class'),
      },
      {
        accessorKey: 'harvest_type',
        header: () => t('harvest.type'),
        cell: ({ row }) => {
          return (
            <p>
              {harvestType?.find(
                (v) => v?.value === row?.original?.harvest_type,
              )?.label ?? '-'}
            </p>
          );
        },
      },
      {
        accessorKey: 'close_party',
        header: () => t('close.party'),
        cell: ({ row }) => {
          return (
            <>
              {row?.original?.is_closed ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setPartyDetail(row?.original);
                    openModal();
                  }}
                >
                  {t('open.party')}
                </Button>
              ) : (
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => {
                    setPartyDetail(row?.original);
                    openModal();
                  }}
                >
                  <PackageMinusIcon className="text-muted-foreground" />
                </Button>
              )}
            </>
          );
        },
      },
      {
        id: 'action',
        header: () => <div className="text-cente">{t('action')}</div>,
        cell: ({ row }) => (
          <div className="flex-center-center">
            <Button
              size="icon"
              variant="ghost"
              className="size-8 !rounded-10"
              onClick={() =>
                navigate(ROUTE_PARTIES_EDIT + '/' + row.original.id)
              }
            >
              <EditIcon />
            </Button>
          </div>
        ),
      },
    ],
    [cropGenerations, cropTypes, generationTypes],
  );
};
